import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useMediaQueryContext } from '@fourthwall/components'
import { useOnceInView, useTagManager } from '@hooks'
import { routing } from '@utils/routing'

import { useRecommendationsDismiss } from '../../../Recommendations/hooks/useRecommendationsDismiss'
import type { MiniRecommendationSlideProps } from '../../MiniRecommendations.type'
import * as Carousel from '../../components/Carousel/Carousel'
import { MiniRecommendationDismissButton } from '../../components/MiniRecommendationDismissButton/MiniRecommendationDismissButton'

const ID = 'ytShelf' as const

const CarouselItem = styled(Carousel.Item)`
  background-color: rgba(255, 74, 243, 0.15);

  &:hover {
    background-color: rgba(255, 74, 243, 0.25);
  }
`

export const YTShelfSlide = ({
  data,
  'data-role': dataRole,
  resetSlider,
}: MiniRecommendationSlideProps) => {
  const navigate = useNavigate()
  const tagManager = useTagManager()
  const { tablet } = useMediaQueryContext()
  const { recommendationsDismiss } = useRecommendationsDismiss()
  const viewRef = useOnceInView(() => {
    tagManager.trigger('mini_recommendation_view', {
      type: ID,
    })
  })

  const hasYouTubeSocialLink = data.youtubeShelf?.socialLinks?.socialNetworks?.some(
    ({ name }) => name === 'youtube',
  )

  const isYouTubeIntegrationConnected =
    data.youtubeShelf?.creatorIntegrations?.youtubeMerchshelf?.status === 'CONNECTED'

  const shouldShowYouTubeShelf = hasYouTubeSocialLink && !isYouTubeIntegrationConnected

  if (!shouldShowYouTubeShelf || !recommendationsDismiss.youTubeProductShelf.visible) {
    return null
  }

  const redirectToYouTubeSettings = () => {
    tagManager.trigger('mini_recommendation_click', {
      type: ID,
    })

    navigate(routing.apps.youTubeProductShelf)
  }

  return (
    <CarouselItem
      ref={viewRef}
      data-role={dataRole}
      data-analytics={ID}
      onClick={redirectToYouTubeSettings}
    >
      <Carousel.ContentLeft>
        <Carousel.ItemEmote>🛍️</Carousel.ItemEmote>
        {!tablet && <Carousel.ItemText>Tip: Add YouTube shelf</Carousel.ItemText>}
        {tablet && (
          <Carousel.ItemText>Tip: Add YouTube shelf to showcase products</Carousel.ItemText>
        )}
      </Carousel.ContentLeft>

      {recommendationsDismiss.youTubeProductShelf.visible && (
        <MiniRecommendationDismissButton
          onDismiss={() => {
            recommendationsDismiss.youTubeProductShelf.dismiss()
            tagManager.trigger('mini_recommendation_dismiss', {
              type: ID,
            })
            setTimeout(() => {
              resetSlider()
            }, 0)
          }}
        />
      )}
    </CarouselItem>
  )
}
